import { useECharts } from '@kbox-labs/react-echarts';
import { useEffect } from 'react';

import { VFlex, VStack } from '@/package';

import type { PieChartData } from './type';

export const SelectablePieChart = <T extends PieChartData>({
  pieData,
  selectedValue,
  onSelect,
}: {
  selectedValue: number | null;
  pieData: T[];
  onSelect: (index: number) => void;
}) => {
  const dataWithSelection = pieData.map((item, index) => ({
    ...item,
    value: item.aiCount + item.humanCount,
    selected: selectedValue === index,
    itemStyle: {
      opacity:
        typeof selectedValue === 'number' && selectedValue !== index ? 0.3 : 1,
    },
  }));
  const isNoneData = dataWithSelection.every(item => item.value === 0);

  const totalValue = dataWithSelection.reduce(
    (sum, item) => sum + item.value,
    0,
  );

  const [ref, echartsInstance] = useECharts<HTMLDivElement>({
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        if (!params) return '';
        const data = params.data;
        const value = data.value || 0;
        const percentage =
          totalValue > 0 ? Math.round((value / totalValue) * 100) : 0;
        const formattedValue = value.toLocaleString();
        return `<div style="font-weight: 600;">${data.name}</div><div style="text-align: right;">${formattedValue} <span style="color: #D3D3D3;">|</span> ${percentage.toString().padStart(2, '0')}%</div>`;
      },
      backgroundColor: '#fff',
      borderColor: '#B8E3F4',
      borderWidth: 1,
      borderRadius: 4,
      padding: [4, 8],
      textStyle: {
        color: '#333',
        fontSize: 12,
      },
    },
    series: [
      {
        type: 'pie',
        radius: ['0%', '90%'],
        center: ['50%', '50%'],
        label: { show: false },
        labelLine: { show: false },
        selectedMode: 'single',
        emphasis: {
          scale: false,
          focus: 'self',
          itemStyle: {
            opacity: 1,
          },
        },
        blur: {
          itemStyle: {
            opacity: 0.3,
          },
        },
        data: isNoneData ? undefined : dataWithSelection,
      },
    ],
    color: dataWithSelection.map(item => item.color),
  });

  useEffect(() => {
    if (echartsInstance) {
      echartsInstance.resize();
    }
  }, [echartsInstance]);

  useEffect(() => {
    if (echartsInstance) {
      echartsInstance.dispatchAction({
        type: 'select',
        dataIndex: selectedValue,
      });
    }
  }, [selectedValue]);

  useEffect(() => {
    echartsInstance?.on('selectchanged', (params: any) => {
      onSelect(params.selected.at(0)?.dataIndex.at(0));
    });

    return () => {
      echartsInstance?.off('selectchanged');
    };
  }, [echartsInstance]);

  return (
    <VFlex
      className="h-[241px] w-[266px] py-4"
      justify="center"
      items="center"
      gap={1}
    >
      <VStack gap={0.5}>
        <div className="text-body-xl font-semibold">Base Class</div>
        <VFlex className="size-full" justify="center" items="center">
          <div ref={ref} style={{ width: '200px', height: '200px' }} />
        </VFlex>
      </VStack>
    </VFlex>
  );
};
